/**
 * Shell page header (APP-040) — Ionic/Capacitor ownership.
 *
 * Customer-app shell tabs must use this `PageHeader` (Ion back stack, Cap-aware
 * SidebarTrigger, pin-on-scroll). Never import ui `ShellHeader` for shell tabs —
 * that primitive is browser-safe chrome for admin/other Vite apps only.
 *
 * Mobile: a top bar with only the back / sidebar control (left) and inline
 * actions (right); the page title sits below it and scrolls away with the page.
 * Chrome icon buttons (back, SidebarTrigger, overflow) are always circular
 * 44px chips. The bar alone pins on scroll and reappears on scroll up over a
 * transparent gradient (Apple-style). Desktop: one row — chrome, breadcrumb +
 * title, actions.
 */
import { useIonRouter } from '@ionic/react';
import { Button, cn } from '@up2stay/up2promo-ui';
import { ChevronLeft } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { SidebarTrigger } from '@/core/components/navigation/sidebar';
import { useIsMobile } from '@/core/hooks/use-mobile';
import { useScrollPinnedHeader } from '@/core/hooks/use-scroll-pinned-header';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { canonicalizeShellPathname, shellPaths } from '@/core/router/app-paths';
import { isMoreTabSubPath } from '@/core/router/app-tabs';
import {
  getShellBackTarget,
  isShellNavigationRoot,
  shouldShowShellBackButton,
} from '@/core/router/shell-route-config';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';

/** Shared class for circular PageHeader icon chrome (back, sidebar, overflow). */
export const PAGE_HEADER_CHROME_BUTTON_CLASS =
  'page-header-chrome-button size-11 shrink-0 rounded-full shadow-none';

type PageHeaderProps = {
  title: string;
  description?: string;
  /** Narrow-viewport subtitle; falls back to `description`. */
  descriptionShort?: string;
  /** Small context line above the title on mobile (desktop uses the breadcrumb). */
  eyebrow?: ReactNode;
  /** Optional eyebrow / breadcrumb above the title (handover Profiles header). */
  breadcrumb?: ReactNode;
  actions?: ReactNode;
  /**
   * `inline` (default): compact actions stay on the title row.
   * `stack`: primary CTAs sit under title + description on small screens.
   */
  actionsLayout?: 'inline' | 'stack';
  leading?: ReactNode;
  /** Fallback route when the Ionic stack cannot go back. */
  backHref?: string;
  onBack?: () => void;
  /**
   * Force-hide back chrome (More hub / other tab roots). Wins over pathname
   * lag during Ionic pop so a child route’s RR path cannot flash a back control
   * on the root page.
   */
  hideBack?: boolean;
  /**
   * Hide the title / breadcrumb / description block (chrome bar + actions only).
   * Used by mobile lead detail comps that put identity in the page body.
   */
  hideTitle?: boolean;
  leftActions?: ReactNode;
  className?: string;
};

function resolveHeaderPathname(
  ionPathname: string | undefined,
  locationPathname: string,
): string {
  // Prefer React Router — it matches the mounted page on first paint. Ionic
  // `routeInfo.pathname` can lag one frame after More → subpage pushes and
  // wrongly hides hierarchy back until the stack settles.
  return canonicalizeShellPathname(locationPathname || ionPathname || '');
}

function computeShowBack(
  pathname: string,
  isNative: boolean,
  hasExplicitBack: boolean,
  backHref: string | undefined,
  hideBack: boolean,
): boolean {
  if (hideBack) return false;
  // Hard stop on tab / More roots even when RR still points at a child during pop.
  if (isShellNavigationRoot(pathname, isNative)) return false;

  const isMoreSubPage = isMoreTabSubPath(pathname);
  const isMoreHubBackTarget =
    backHref === shellPaths.moreHub || backHref === shellPaths.more;
  const hierarchyBack = shouldShowShellBackButton(pathname, isNative);

  if (isNative) {
    return hasExplicitBack || hierarchyBack;
  }

  return (
    hasExplicitBack && !isMoreHubBackTarget && (!isMoreSubPage || hierarchyBack)
  );
}

export function PageHeader({
  title,
  description,
  descriptionShort,
  eyebrow,
  breadcrumb,
  actions,
  actionsLayout = 'inline',
  leading,
  backHref,
  onBack,
  hideBack = false,
  hideTitle = false,
  leftActions,
  className,
}: PageHeaderProps) {
  const dict = useDictionary();
  const ionRouter = useIonRouter();
  const location = useLocation();
  const headerRef = useRef<HTMLElement>(null);
  const isNativeShell = isCapacitorNative();
  // Narrow viewports (browser or native): the top bar hides on scroll down and
  // returns on scroll up. Wider layouts keep the single static row.
  const isMobileViewport = useIsMobile();
  const usesScrollPin = isMobileViewport;
  // Browser shells always get the SidebarTrigger: on narrow viewports it opens
  // the navigation drawer, the only way between areas there (no tab bar on
  // the web, DEV-552). Native uses the floating bottom tab bar instead.
  const showSidebarTrigger = !isNativeShell;
  const hasExplicitBack = Boolean(backHref || onBack);

  const pathname = resolveHeaderPathname(
    ionRouter.routeInfo?.pathname,
    location.pathname,
  );
  const hierarchyBackHref = isNativeShell
    ? getShellBackTarget(pathname, true)
    : getShellBackTarget(pathname, false);
  const resolvedBackHref = backHref ?? hierarchyBackHref;

  const showBack = computeShowBack(
    pathname,
    isNativeShell,
    hasExplicitBack,
    backHref,
    hideBack,
  );
  const backAriaLabel = dict?.ui?.back ?? 'Back';
  const [headerHeight, setHeaderHeight] = useState(0);
  const { pinned, revealed, animate } = useScrollPinnedHeader(
    headerRef,
    headerHeight,
    usesScrollPin,
  );

  useEffect(() => {
    const headerEl = headerRef.current;
    if (!headerEl || pinned) {
      return;
    }

    const measure = () => {
      const nextHeight = headerEl.offsetHeight;
      if (nextHeight > 0) {
        setHeaderHeight(nextHeight);
      }
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(headerEl);

    return () => {
      resizeObserver.disconnect();
    };
  }, [
    pinned,
    breadcrumb,
    title,
    description,
    descriptionShort,
    actions,
    actionsLayout,
    leading,
    leftActions,
    showBack,
  ]);

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    // Prefer declared hierarchy over Ionic history — tab switches pollute canGoBack().
    if (resolvedBackHref) {
      ionRouter.push(resolvedBackHref, 'back', 'pop');
      return;
    }
    if (ionRouter.canGoBack()) {
      ionRouter.goBack();
    }
  };

  const stacked = actionsLayout === 'stack';
  const inlineActions = !stacked && actions ? actions : null;

  const backButton = showBack ? (
    <Button
      type="button"
      variant="outline"
      size="icon"
      mode="icon"
      onClick={handleBack}
      aria-label={backAriaLabel}
      className={cn('page-header-back-button', PAGE_HEADER_CHROME_BUTTON_CLASS)}
    >
      <ChevronLeft className="size-[18px]" strokeWidth={2.25} />
    </Button>
  ) : null;

  const sidebarTrigger = showSidebarTrigger ? (
    <SidebarTrigger
      variant="outline"
      size="icon"
      mode="icon"
      className={cn(
        'page-header-sidebar-trigger',
        PAGE_HEADER_CHROME_BUTTON_CLASS,
      )}
    />
  ) : null;

  // Separate circular chips (not ButtonGroup) — joined group would square the corners.
  const chromeControls =
    backButton && sidebarTrigger ? (
      <div className="flex shrink-0 items-center gap-2">
        {backButton}
        {sidebarTrigger}
      </div>
    ) : (
      (backButton ?? sidebarTrigger)
    );

  const hasBarContent = Boolean(chromeControls || leftActions || inlineActions);

  return (
    <div
      className={cn(
        // Mobile: bar row, then title. md+: one row (bar is `contents`).
        'page-header grid min-w-0 grid-cols-[minmax(0,1fr)] md:grid-cols-[auto_minmax(0,1fr)_auto] md:items-center md:gap-x-3',
        // Notch / status bar: always on the wrapper (the bar may not render).
        isNativeShell && 'pt-[env(safe-area-inset-top,0px)]',
        className,
      )}
    >
      {hasBarContent ? (
        <div
          className="page-header-slot md:contents"
          style={
            pinned && headerHeight > 0 ? { height: headerHeight } : undefined
          }
        >
          <header
            ref={headerRef}
            className={cn(
              'page-header-bar flex min-w-0 items-center justify-between gap-2 py-1.5 md:contents',
              showSidebarTrigger && 'pt-3',
              pinned && 'page-header-pinned',
              pinned && animate && 'page-header-pinned--animate',
              pinned && revealed && 'page-header-pinned--visible',
            )}
          >
            <div className="flex min-w-0 shrink-0 items-center gap-2 md:col-start-1 md:row-start-1 md:pt-5">
              {chromeControls}
              {leftActions}
            </div>
            {inlineActions ? (
              <div className="flex shrink-0 items-center gap-2 md:col-start-3 md:row-start-1 md:pt-5">
                {inlineActions}
              </div>
            ) : null}
          </header>
        </div>
      ) : null}

      {hideTitle ? null : (
        <div
          className={cn(
            'flex min-w-0 items-start gap-2.5 px-0.5 pb-1 pt-2 md:col-start-2 md:row-start-1 md:gap-3 md:px-1 md:pb-2.5 md:pt-5',
            !hasBarContent && 'md:col-start-1 md:col-span-2',
          )}
        >
          {leading != null ? <div className="shrink-0">{leading}</div> : null}
          <div className="min-w-0 flex-1">
            {breadcrumb ? (
              <div className="mb-1.5 hidden text-xs font-medium leading-none tracking-wide text-muted-foreground md:block">
                {breadcrumb}
              </div>
            ) : null}
            {eyebrow ? (
              <div className="mb-0.5 text-[12.5px] text-muted-foreground md:hidden">
                {eyebrow}
              </div>
            ) : null}
            <h1 className="text-balance text-[30px] font-bold leading-[1.1] tracking-[-0.02em] line-clamp-2 md:truncate md:text-[22px] md:leading-[1.15] md:tracking-[-0.4px] md:line-clamp-none">
              {title}
            </h1>
            {description ? (
              <>
                <p className="mt-1.5 hidden text-pretty text-[13.5px] leading-snug text-muted-foreground md:block">
                  {description}
                </p>
                <p className="mt-1 line-clamp-2 text-pretty text-[13px] leading-snug text-muted-foreground md:hidden">
                  {descriptionShort ?? description}
                </p>
              </>
            ) : null}
          </div>
        </div>
      )}

      {stacked && actions ? (
        <div className="flex w-full min-w-0 items-center gap-2 pb-1 pt-2 md:col-start-3 md:row-start-1 md:w-auto md:pt-5">
          {actions}
        </div>
      ) : null}
    </div>
  );
}
